import { MessageSquareX, Undo2 } from "lucide-react";

/**
 * The REASON on a card — a rejection remark or a rework note, where the
 * decision itself is shown, not only on the detail page. The audit's F344/F359
 * cluster was exactly this gap: a rejected claim on a list gave the filer no
 * why, and a claim bounced for rework looked identical to one nobody had
 * touched.
 *
 * Field names differ per module (expenses write `am_remark`, leaves and tour
 * plans `approver_remark`), so the fallback chain lives here once.
 */
export default function DecisionNotes({ record, className = "" }) {
  const status = String(record?.status ?? "").toLowerCase();
  const rejectionReason =
    status === "rejected"
      ? record?.approver_remark || record?.am_remark || null
      : null;
  // A pending row that has been round the loop carries the reason it came
  // back. Cleared stage fields mean the verification no longer stands; the
  // reason is what the next look is about.
  const reworkReason =
    (status === "pending" || status === "submitted") &&
    (record?.rework_count || 0) > 0
      ? record?.rework_reason
      : null;

  if (!rejectionReason && !reworkReason) return null;

  if (rejectionReason) {
    return (
      <p
        className={`text-[11px] leading-tight text-red-700 bg-red-50 border border-red-100 rounded px-2 py-1 flex items-start gap-1 ${className}`}
        data-testid={`rejection-reason-${record.id}`}
      >
        <MessageSquareX size={11} className="shrink-0 mt-[1px]" />
        <span>
          Rejected: <span className="font-medium">{rejectionReason}</span>
        </span>
      </p>
    );
  }

  return (
    <p
      className={`text-[11px] leading-tight text-amber-800 bg-amber-50 border border-amber-200 rounded px-2 py-1 flex items-start gap-1 ${className}`}
      data-testid={`rework-reason-${record.id}`}
    >
      <Undo2 size={11} className="shrink-0 mt-[1px]" />
      <span>
        Sent back for rework
        {record.rework_by_name ? ` by ${record.rework_by_name}` : ""}:{" "}
        <span className="font-medium">{reworkReason || "no reason recorded"}</span>
      </span>
    </p>
  );
}
